"use client"

import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area"
import { cn, getTeeColor } from "@/lib/utils"
import { GolfCourse, type TeeHole } from "@/types/golf"

interface TeeTableProps {
  course: GolfCourse;
  t: (key: string) => string;
}

// This component would replace your current table implementation
export default function TeeTable({ course, t }: TeeTableProps) {
  const tees = course.tees || []

  return (
    <div className="w-full rounded-xl overflow-hidden bg-card shadow-md border dark:border-gray-700">
      {/* Header section */}
      <div className="p-4 bg-primary/80 dark:bg-primary/10">
        <h3 className="text-lg font-bold text-white">{course.courseName}</h3>
        <div className="flex flex-wrap gap-2 text-sm text-muted font-bold mt-1">
          <span>{course.holes || 18} {t('holes')}</span>
          <span>•</span>
          <span>Par {course.par || '-'}</span>
          {/* <span>•</span>
          <span>{course.courseType || '-'}</span> */}
        </div>
      </div>

      <ScrollArea>
        <div className={`p-1 ${tees.length > 3 ? "min-w-max" : "min-w-[640px]"}`}>
          {/* Tee headers */}
          <div className="flex mb-2 px-2">
            <div className="w-12 flex-shrink-0"></div>
            <div className="flex-1 grid grid-cols-2 gap-1 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <div>{t("men")}</div>
              <div></div>
            </div>

            <div className="flex flex-row">
              {tees.map((tee) => (
                <div key={tee.teeId} className="w-[120px] mx-1 flex-shrink-0 text-center">
                  <span
                    className={cn(
                      "inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium",
                      getTeeColor(tee.teeName),
                    )}
                  >
                    {tee.teeName}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex-1 grid grid-cols-2 gap-1 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <div>{t("women")}</div>
              <div></div>
            </div>
          </div>

          {/* Sub-header */}
          <div className="flex mb-3 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            <div className="w-12 flex-shrink-0 text-center">{t("hole")}</div>
            <div className="flex-1 grid grid-cols-2 gap-1 text-center">
              <div>{t("par")}</div>
              <div>{t("si")}</div>
            </div>

            {tees.map((tee) => (
              <div key={tee.teeId} className="w-[120px] mx-1 flex-shrink-0 text-center">
                {t("distance")}
              </div>
            ))}

            <div className="flex-1 grid grid-cols-2 gap-1 text-center">
              <div>{t("par")}</div>
              <div>{t("si")}</div>
            </div>
          </div>

          {/* Front nine */}
          <div className="space-y-1 mb-3">
            {Array.from({ length: Math.min(9, course.holes || 18) }, (_, i) => i + 1).map((holeNumber) => (
              <div key={holeNumber} className="flex items-center px-2 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
                <div className="w-12 flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-200 font-medium mx-auto">
                    {holeNumber}
                  </div>
                </div>

                <div className="flex-1 grid grid-cols-2 gap-1 text-center">
                  <div className="text-gray-700 dark:text-gray-200">
                    {tees[0]?.holes.find((h: TeeHole) => h.holeNumber === holeNumber)?.par || "-"}
                  </div>
                  <div className="text-gray-700 dark:text-gray-200">
                    {tees[0]?.holes.find((h: TeeHole) => h.holeNumber === holeNumber)?.handicap || "-"}
                  </div>
                </div>

                <div className="flex flex-row">
                  {tees.map((tee) => {
                    const holeData = tee.holes.find((h: TeeHole) => h.holeNumber === holeNumber)
                    return (
                      <div
                        key={`${tee.teeId}-${holeNumber}`}
                        className={cn(
                          "w-[120px] mx-1 flex-shrink-0 h-8 rounded flex items-center justify-center text-xs font-medium",
                          getTeeColor(tee.teeName),
                        )}
                      >
                        {holeData?.distance || "-"}
                      </div>
                    )
                  })}
                </div>

                <div className="flex-1 grid grid-cols-2 gap-1 text-center">
                  <div className="text-gray-700 dark:text-gray-200">
                    {tees[0]?.holes.find((h: TeeHole) => h.holeNumber === holeNumber)?.parW ||
                      tees[0]?.holes.find((h: TeeHole) => h.holeNumber === holeNumber)?.par ||
                      "-"}
                  </div>
                  <div className="text-gray-700 dark:text-gray-200">
                    {tees[0]?.holes.find((h: TeeHole) => h.holeNumber === holeNumber)?.handicapW ||
                      tees[0]?.holes.find((h: TeeHole) => h.holeNumber === holeNumber)?.handicap ||
                      "-"}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Out total */}
          {course.holes && course.holes > 9 && (
            <div className="flex items-center px-2 py-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg mb-3">
              <div className="w-12 flex-shrink-0">
                <div className="text-center font-medium text-gray-700 dark:text-gray-200">{t("out")}</div>
              </div>

              <div className="flex-1 grid grid-cols-2 gap-1 text-center">
                <div className="font-medium text-gray-700 dark:text-gray-200">
                  {tees[0]?.holes
                    .filter((h: TeeHole) => h.holeNumber <= 9)
                    .reduce((sum, hole) => sum + (hole.par || 0), 0) || "-"}
                </div>
                <div></div>
              </div>

              <div className="flex flex-row">
                {tees.map((tee) => (
                  <div
                    key={`${tee.teeId}-out`}
                    className="w-[120px] mx-1 flex-shrink-0 text-center font-medium text-gray-700 dark:text-gray-200"
                  >
                    {tee.holes.filter((h: TeeHole) => h.holeNumber <= 9).reduce((sum, hole) => sum + (hole.distance || 0), 0) || "-"}
                  </div>
                ))}
              </div>

              <div className="flex-1 grid grid-cols-2 gap-1 text-center">
                <div className="font-medium text-gray-700 dark:text-gray-200">
                  {tees[0]?.holes
                    .filter((h: TeeHole) => h.holeNumber <= 9)
                    .reduce((sum, hole) => sum + (hole.parW || hole.par || 0), 0) || "-"}
                </div>
                <div></div>
              </div>
            </div>
          )}

          {/* Back nine (if applicable) */}
          {(course.holes || 18) > 9 && (
            <>
              <div className="space-y-1 mb-3">
                {Array.from({ length: Math.min(9, (course.holes || 18) - 9) }, (_, i) => i + 10).map((holeNumber) => (
                  <div key={holeNumber} className="flex items-center px-2 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
                    <div className="w-12 flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-200 font-medium mx-auto">
                        {holeNumber}
                      </div>
                    </div>

                    <div className="flex-1 grid grid-cols-2 gap-1 text-center">
                      <div className="text-gray-700 dark:text-gray-200">
                        {tees[0]?.holes.find((h: TeeHole) => h.holeNumber === holeNumber)?.par || "-"}
                      </div>
                      <div className="text-gray-700 dark:text-gray-200">
                        {tees[0]?.holes.find((h: TeeHole) => h.holeNumber === holeNumber)?.handicap || "-"}
                      </div>
                    </div>

                    <div className="flex flex-row">
                      {tees.map((tee) => {
                        const holeData = tee.holes.find((h: TeeHole) => h.holeNumber === holeNumber)
                        return (
                          <div
                            key={`${tee.teeId}-${holeNumber}`}
                            className={cn(
                              "w-[120px] mx-1 flex-shrink-0 h-8 rounded flex items-center justify-center text-xs font-medium",
                              getTeeColor(tee.teeName),
                            )}
                          >
                            {holeData?.distance || "-"}
                          </div>
                        )
                      })}
                    </div>

                    <div className="flex-1 grid grid-cols-2 gap-1 text-center">
                      <div className="text-gray-700 dark:text-gray-200">
                        {tees[0]?.holes.find((h: TeeHole) => h.holeNumber === holeNumber)?.parW ||
                          tees[0]?.holes.find((h: TeeHole) => h.holeNumber === holeNumber)?.par ||
                          "-"}
                      </div>
                      <div className="text-gray-700 dark:text-gray-200">
                        {tees[0]?.holes.find((h: TeeHole) => h.holeNumber === holeNumber)?.handicapW ||
                          tees[0]?.holes.find((h: TeeHole) => h.holeNumber === holeNumber)?.handicap ||
                          "-"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* In total */}
              <div className="flex items-center px-2 py-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg mb-3">
                <div className="w-12 flex-shrink-0">
                  <div className="text-center font-medium text-gray-700 dark:text-gray-200">{t("in")}</div>
                </div>

                <div className="flex-1 grid grid-cols-2 gap-1 text-center">
                  <div className="font-medium text-gray-700 dark:text-gray-200">
                    {tees[0]?.holes
                      .filter((h: TeeHole) => h.holeNumber > 9)
                      .reduce((sum, hole) => sum + (hole.par || 0), 0) || "-"}
                  </div>
                  <div></div>
                </div>

                <div className="flex flex-row">
                  {tees.map((tee) => (
                    <div
                      key={`${tee.teeId}-in`}
                      className="w-[120px] mx-1 flex-shrink-0 text-center font-medium text-gray-700 dark:text-gray-200"
                    >
                      {tee.holes.filter((h: TeeHole) => h.holeNumber > 9).reduce((sum, hole) => sum + (hole.distance || 0), 0) ||
                        "-"}
                    </div>
                  ))}
                </div>

                <div className="flex-1 grid grid-cols-2 gap-1 text-center">
                  <div className="font-medium text-gray-700 dark:text-gray-200">
                    {tees[0]?.holes
                      .filter((h: TeeHole) => h.holeNumber > 9)
                      .reduce((sum, hole) => sum + (hole.parW || hole.par || 0), 0) || "-"}
                  </div>
                  <div></div>
                </div>
              </div>
            </>
          )}

          {/* Total */}
          <div className="flex items-center px-2 py-4 bg-secondary dark:bg-secondary/30 rounded-lg">
            <div className="w-12 flex-shrink-0">
              <div className="text-center font-bold text-secondary-foreground dark:text-secondary-foreground/90">{t("total")}</div>
            </div>

            <div className="flex-1 grid grid-cols-2 gap-1 text-center">
              <div className="font-bold text-secondary-foreground dark:text-secondary-foreground/90">
                {tees[0]?.holes.reduce((sum, hole) => sum + (hole.par || 0), 0) || "-"}
              </div>
              <div></div>
            </div>

            <div className="flex flex-row">
              {tees.map((tee) => (
                <div
                  key={`${tee.teeId}-total`}
                  className={cn(
                    "w-[120px] flex-shrink-0 text-center py-2 mx-1 rounded-md font-bold",
                    getTeeColor(tee.teeName)
                  )}
                >
                  {tee.holes.reduce((sum, hole) => sum + (hole.distance || 0), 0) || "-"}
                </div>
              ))}
            </div>

            <div className="flex-1 grid grid-cols-2 gap-1 text-center">
              <div className="font-bold text-secondary-foreground dark:text-secondary-foreground/90">
                {tees[0]?.holes.reduce((sum, hole) => sum + (hole.parW || hole.par || 0), 0) || "-"}
              </div>
              <div className="font-bold text-secondary-foreground dark:text-secondary-foreground/90">
                {tees[0]?.holes.reduce((sum, hole) => sum + (hole.handicapW || hole.handicap || 0), 0) || "-"}
              </div>
            </div>
          </div>
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  )
}
